import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen, Plus, Search, Edit3, Trash2, Eye, EyeOff,
  Tag, RefreshCw, Upload, FileText, Sparkles,
} from "lucide-react";

const CATEGORY_LABELS = {
  governing_docs: "Governing Docs",
  rules_regs:     "Rules & Regs",
  amenities:      "Amenities",
  payments:       "Payments & Dues",
  maintenance:    "Maintenance",
  governance:     "Governance",
  faq:            "FAQ",
  other:          "Other",
};

const CATEGORY_TONE = {
  governing_docs: "border-fuchsia-500/40 text-fuchsia-300",
  rules_regs:     "border-orange-500/40 text-orange-300",
  amenities:      "border-sky-500/40 text-sky-300",
  payments:       "border-emerald-500/40 text-emerald-300",
  maintenance:    "border-amber-500/40 text-amber-300",
  governance:     "border-purple-500/40 text-purple-300",
  faq:            "border-white/15 text-zinc-300",
  other:          "border-zinc-500/40 text-zinc-400",
};

// -----------------------------------------------------------------------------
function ArticleDialog({ propertyId, article, trigger, onSaved }) {
  const editing = !!article;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(article || {
    title: "", category: "faq", tags: "",
    body: "", citation_key: "", published: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(article
        ? { ...article, tags: (article.tags || []).join(", ") }
        : { title: "", category: "faq", tags: "",
             body: "", citation_key: "", published: true });
    }
  }, [open, article]);

  const submit = async () => {
    if (!editing && !propertyId) return toast.error("Pick a property first.");
    setBusy(true);
    try {
      const body = {
        title: form.title,
        category: form.category,
        tags: (form.tags || "").split(/[,;]/).map((s) => s.trim()).filter(Boolean),
        body: form.body,
        citation_key: form.citation_key,
        published: form.published,
      };
      if (editing) {
        await api.patch(`/admin/kb/${article.id}`, body);
        toast.success("Article updated");
      } else {
        await api.post("/admin/kb", { ...body, property_id: propertyId });
        toast.success("Article created");
      }
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? "Edit article" : "New KB article"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="Pool hours & etiquette"
                   className="bg-white/[0.04] border-white/10" data-testid="kb-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="kb-cat"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                  {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Citation key <span className="text-zinc-500">(shown to residents when cited)</span></Label>
              <Input value={form.citation_key || ""}
                     onChange={(e) => setForm({ ...form, citation_key: e.target.value })}
                     placeholder="CC&R §4.2"
                     className="bg-white/[0.04] border-white/10" data-testid="kb-citation" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Tags (comma-separated)</Label>
            <Input value={form.tags || ""}
                   onChange={(e) => setForm({ ...form, tags: e.target.value })}
                   placeholder="pool, hours, amenities"
                   className="bg-white/[0.04] border-white/10" />
          </div>
          <div>
            <Label className="text-xs">Body (markdown supported) *</Label>
            <Textarea rows={8} value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      placeholder="Pool hours are 8 AM to 10 PM daily…"
                      className="bg-white/[0.04] border-white/10 font-mono text-sm" data-testid="kb-body" />
            <div className="text-[11px] text-zinc-500 mt-1">
              {(form.body || "").length} chars — Claude will inject up to 1500 chars per article into its context.
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.02] p-3">
            <div>
              <div className="text-sm font-semibold text-white">Published</div>
              <div className="text-xs text-zinc-500">Unpublished drafts are hidden from residents and skipped by the AI Concierge.</div>
            </div>
            <Switch checked={!!form.published}
                    onCheckedChange={(v) => setForm({ ...form, published: v })}
                    data-testid="kb-published" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-white/10" onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={busy || !form.title || !form.body} onClick={submit}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="kb-save">
            {busy ? "Saving…" : editing ? "Save changes" : "Create article"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
function CsvImportDialog({ propertyId, onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file || !propertyId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/kb/import-csv?property_id=${encodeURIComponent(propertyId)}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`Imported ${data.created} article${data.created === 1 ? "" : "s"}${data.skipped ? ` (${data.skipped} duplicates skipped)` : ""}`);
      setOpen(false); setFile(null);
      onImported();
    } catch (e) { toast.error(apiErrorMessage(e) || "Import failed"); }
    finally { setBusy(false); }
  };

  const template = "title,body,category,tags,citation_key,published\nPool Hours,Pool hours are 8 AM to 10 PM daily.,amenities,pool;hours,CC&R §4.2,true\n";
  const dl = () => {
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "prestige-kb-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-white/10" data-testid="kb-import-btn">
          <Upload className="h-3.5 w-3.5 mr-1" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white max-w-md">
        <DialogHeader><DialogTitle>Bulk-import KB articles</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-zinc-400">
            CSV columns: <code className="text-zinc-200">title, body</code> (required),
            {" "}<code className="text-zinc-200">category, tags, citation_key, published</code> (optional).
            Dedupes by title.
          </div>
          <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])}
                 className="text-xs w-full" data-testid="kb-import-file" />
          <Button variant="outline" size="sm" onClick={dl} className="border-white/10 text-xs">
            <FileText className="h-3 w-3 mr-1" /> Download template
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-white/10" onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={!file || busy || !propertyId} onClick={submit}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="kb-import-submit">
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
export default function KnowledgeBasePage() {
  const { propertyId, currentProperty } = useProperty();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState({ category: "all", q: "" });

  const load = useCallback(async () => {
    try {
      const params = { property_id: propertyId || undefined };
      if (filter.category !== "all") params.category = filter.category;
      const { data } = await api.get("/admin/kb", { params });
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  }, [propertyId, filter.category]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => {
    if (!data) return [];
    const q = filter.q.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((a) =>
      a.title.toLowerCase().includes(q) ||
      (a.body || "").toLowerCase().includes(q) ||
      (a.tags || []).some((t) => t.includes(q))
    );
  }, [data, filter.q]);

  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/kb/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  const k = data?.kpis || {};

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="AI & Content"
        title="Knowledge Base"
        subtitle={currentProperty
          ? `Community docs & FAQs that the AI Concierge cites for ${currentProperty.name}.`
          : "Articles the AI Concierge quotes when residents ask about your community."}
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="border-white/10" onClick={load} data-testid="kb-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
            <CsvImportDialog propertyId={propertyId} onImported={load} />
            <ArticleDialog
              propertyId={propertyId}
              onSaved={load}
              trigger={
                <Button className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="kb-new-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> New article
                </Button>
              }
            />
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border-white/10 bg-card" data-testid="kb-kpi-total">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total articles</div>
          <div className="text-2xl font-semibold text-white mt-1 font-display">{k.total || 0}</div>
        </Card>
        <Card className="p-4 border-emerald-500/20 bg-emerald-500/[0.04]" data-testid="kb-kpi-published">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">Published (Claude sees)</div>
          <div className="text-2xl font-semibold text-white mt-1 font-display">{k.published || 0}</div>
        </Card>
        <Card className="p-4 border-amber-500/20 bg-amber-500/[0.04]" data-testid="kb-kpi-drafts">
          <div className="text-[10px] uppercase tracking-widest text-amber-300">Drafts</div>
          <div className="text-2xl font-semibold text-white mt-1 font-display">{k.drafts || 0}</div>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-zinc-500" />
          <Input value={filter.q}
                 onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                 placeholder="Search titles, body, tags…"
                 className="pl-8 bg-white/[0.04] border-white/10 w-72" data-testid="kb-search" />
        </div>
        <Button size="sm" onClick={() => setFilter({ ...filter, category: "all" })}
                variant={filter.category === "all" ? "default" : "outline"}
                className={filter.category === "all" ? "bg-orange-500 hover:bg-orange-600" : "border-white/10"}>
          All
        </Button>
        {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
          <Button key={k} size="sm"
                  onClick={() => setFilter({ ...filter, category: k })}
                  variant={filter.category === k ? "default" : "outline"}
                  className={filter.category === k ? "bg-orange-500 hover:bg-orange-600" : "border-white/10"}
                  data-testid={`kb-cat-${k}`}>
            {l}
          </Button>
        ))}
      </div>

      {items.length === 0 ? (
        <Card className="p-10 border-white/10 bg-card text-center" data-testid="kb-empty">
          <BookOpen className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
          <div className="text-sm text-zinc-400">
            {data?.items?.length === 0
              ? "No articles yet — click New article to start grounding your AI Concierge on real community rules."
              : "No matches for your filter."}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((a) => (
            <Card key={a.id} className="p-4 border-white/10 bg-card" data-testid={`kb-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className={CATEGORY_TONE[a.category] || CATEGORY_TONE.other}>
                      {CATEGORY_LABELS[a.category] || a.category}
                    </Badge>
                    {!a.published && (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-300">
                        <EyeOff className="h-3 w-3 mr-1" />Draft
                      </Badge>
                    )}
                    {a.citation_key && (
                      <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px]">
                        📎 {a.citation_key}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-white truncate">{a.title}</div>
                </div>
              </div>
              <p className="text-xs text-zinc-400 line-clamp-3">{a.summary || a.body}</p>
              {(a.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {a.tags.map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.03] text-zinc-400">
                      <Tag className="h-2.5 w-2.5 inline mr-0.5" />{t}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between text-[11px] text-zinc-500">
                <div className="flex items-center gap-3">
                  <span><Eye className="h-3 w-3 inline mr-0.5" />{a.view_count || 0} views</span>
                  <span><Sparkles className="h-3 w-3 inline mr-0.5" />{a.cited_count || 0} cites</span>
                </div>
                <div className="flex gap-1">
                  <ArticleDialog
                    propertyId={propertyId}
                    article={a}
                    onSaved={load}
                    trigger={
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" data-testid={`kb-edit-${a.id}`}>
                        <Edit3 className="h-3 w-3 mr-1" /> Edit
                      </Button>
                    }
                  />
                  <Button size="sm" variant="ghost"
                          onClick={() => del(a.id)}
                          className="h-6 px-2 text-[11px] text-red-300 hover:text-red-200"
                          data-testid={`kb-delete-${a.id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
